import { Route, Navigate } from "react-router";

import RemountOnFilterView from "@/features/accounting/components/saved-views/RemountOnFilterView";
import FeatureProtectedRoute from "@/features/auth/guards/FeatureProtectedRoute";
import PrivateRoute from "@/features/auth/guards/PrivateRoute";
import ParametersIndexRedirect from "@/features/organization-settings/shared/components/ParametersIndexRedirect";
import ParametersLayout from "@/features/organization-settings/shared/components/ParametersLayout";
import { CampaignTeaserVisit } from "@/shared/components/CampaignTeaserModal/CampaignTeaserVisit";
import { CampaignVisitBoundary } from "@/shared/components/CampaignTeaserModal/CampaignVisitBoundary";
import RouteNotFoundPage from "@/shared/components/ErrorPage/RouteNotFoundPage";
import Layout from "@/shared/components/Layout/index";
import LayoutWithoutSidebar from "@/shared/components/LayoutWithoutSidebar/index";
import StandaloneLayout from "@/shared/components/StandaloneLayout/index";

// Pages are lazy-loaded so the initial bundle stays small.
// Each route only downloads its own chunk on navigation.
const AccountingLedgerPage = lazyWithRetry(() => import("@/features/accounting/pages/AccountingLedgerPage"));
const AccountingMovementsPage = lazyWithRetry(() => import("@/features/accounting/pages/AccountingMovementsPage"));
const AccountingBudgetPage = lazyWithRetry(() => import("@/features/accounting/pages/AccountingBudgetPage"));
const AccountingSettingsLayout = lazyWithRetry(() => import("@/features/accounting/components/settings/AccountingSettingsLayout"));
const AccountingFiscalYearPage = lazyWithRetry(() => import("@/features/accounting/pages/settings/AccountingFiscalYearPage"));
const AccountingBankAccountsPage = lazyWithRetry(() => import("@/features/accounting/pages/settings/AccountingBankAccountsPage"));
const AccountingCategoriesPage = lazyWithRetry(() => import("@/features/accounting/pages/settings/AccountingCategoriesPage"));
const AccountingTagsPage = lazyWithRetry(() => import("@/features/accounting/pages/settings/AccountingTagsPage"));
const AccountingBridgePage = lazyWithRetry(() => import("@/features/accounting/pages/settings/AccountingBridgePage"));
const AccountingPayboxPage = lazyWithRetry(() => import("@/features/accounting/pages/settings/AccountingPayboxPage"));
const AccountingSettlementsPage = lazyWithRetry(() => import("@/features/accounting/pages/AccountingSettlementsPage"));
const AccountingSepaRemittancesPage = lazyWithRetry(() => import("@/features/accounting/pages/AccountingSepaRemittancesPage"));
const HelpPage = lazyWithRetry(() => import("@/features/help/pages/HelpPage"));
const AccountingSynthesisPage = lazyWithRetry(() => import("@/features/accounting/pages/AccountingSynthesisPage"));
const ActivityOperationsPage = lazyWithRetry(() => import("@/features/activity-operations/pages/ActivityOperationsPage").then((m) => ({ default: m.ActivityOperationsPage })));
const ArticlePage = lazyWithRetry(() => import("@/features/blog/pages/ArticlePage"));
const AuditLogsPage = lazyWithRetry(() => import("@/features/audit-logs/pages/AuditLogsPage"));
const BannersPage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/theme-studio/pages/BannersPage"));
const BatchRuleExecutionPage = lazyWithRetry(() => import("@/features/employees/pages/BatchRuleExecutionPage"));
const BeneficiariesPage = lazyWithRetry(() => import("@/features/beneficiaries/pages/BeneficiariesPage"));
const BeneficiaryTypesPage = lazyWithRetry(() => import("@/features/beneficiaries/pages/BeneficiaryTypesPage"));
const BlogPage = lazyWithRetry(() => import("@/features/blog/pages/BlogPage"));
const AdventCalendarDashboardPage = lazyWithRetry(() => import("@/features/advent-calendar/pages/AdventCalendarDashboardPage"));
const AdventCalendarPage = lazyWithRetry(() => import("@/features/advent-calendar/pages/AdventCalendarPage"));
const ArcadePage = lazyWithRetry(() => import("@/features/arcade/pages/ArcadePage"));
const ArcadeInstancePage = lazyWithRetry(() => import("@/features/arcade/pages/ArcadeInstancePage"));
const ArcadeCompetitionPage = lazyWithRetry(() => import("@/features/arcade/pages/ArcadeCompetitionPage"));
const BridgeCallbackPage = lazyWithRetry(() => import("@/features/accounting/pages/BridgeCallbackPage"));
const CalendarPage = lazyWithRetry(() => import("@/features/calendar/pages/CalendarPage"));
const CampaignReportDetailPage = lazyWithRetry(() => import("@/features/communication/pages/CampaignReportDetailPage"));
const CardReissuancesOrderPage = lazyWithRetry(() => import("@/features/card-reissuances/pages/CardReissuancesOrderPage"));
const CardReissuancesPage = lazyWithRetry(() => import("@/features/card-reissuances/pages/CardReissuancesPage"));
const ChangelogsPage = lazyWithRetry(() => import("@/features/changelogs/pages/ChangelogsPage"));
const ChatPage = lazyWithRetry(() => import("@/features/chat/pages/ChatPage"));
const CheckCampaignBeneficiariesPage = lazyWithRetry(() => import("@/features/checks/pages/CheckCampaignBeneficiariesPage"));
const CheckCampaignEmployeesPage = lazyWithRetry(() => import("@/features/checks/pages/CheckCampaignEmployeesPage"));
const ChecksPage = lazyWithRetry(() => import("@/features/checks/pages/ChecksPage"));
const CommunicationPage = lazyWithRetry(() => import("@/features/communication/pages/CommunicationPage"));
const CoordinatesPage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/coordinates/pages/CoordinatesPage"));
const DirectDebitPage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/direct-debit/pages/DirectDebitPage"));
const SubscriptionInvoicesPage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/subscription-invoices/pages/SubscriptionInvoicesPage"));
const CreateOrUpdateArticlePage = lazyWithRetry(() => import("@/features/blog/pages/CreateOrUpdateArticlePage"));
const CrossScopeAutoLoginPage = lazyWithRetry(() => import("@/features/auth/pages/CrossScopeAutoLoginPage"));
const CseMessagesPage = lazyWithRetry(() => import("@/features/cse-messages/pages/CseMessagesPage"));
const CseMessageWizardPage = lazyWithRetry(() => import("@/features/cse-messages/pages/CseMessageWizardPage"));
const CustomFormEditPage = lazyWithRetry(() => import("@/features/custom-forms/pages/CustomFormEditPage"));
const CustomFormSubmissionsPage = lazyWithRetry(() => import("@/features/custom-forms/pages/CustomFormSubmissionsPage"));
const CustomFormsPage = lazyWithRetry(() => import("@/features/custom-forms/pages/CustomFormsPage"));
const DashboardCommunicationPage = lazyWithRetry(() => import("@/features/dashboard-communication/pages/DashboardCommunicationPage"));
const DashboardLayoutEditorPage = lazyWithRetry(() => import("@/features/dashboard-layouts/pages/DashboardLayoutEditorPage"));
const DashboardLayoutsPage = lazyWithRetry(() => import("@/features/dashboard-layouts/pages/DashboardLayoutsPage"));
const DashboardPage = lazyWithRetry(() => import("@/features/dashboard/pages/DashboardPage"));
const DataManagementPage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/data-management/pages/DataManagementPage"));
const ClassifiedAdsPage = lazyWithRetry(() => import("@/features/classified-ads/pages/ClassifiedAdsPage"));
const DealsPage = lazyWithRetry(() => import("@/features/deals/pages/DealsPage"));
const DeliveryModesPage = lazyWithRetry(() => import("@/features/deals/pages/DeliveryModesPage"));
const DiscussionsPage = lazyWithRetry(() => import("@/features/discussions/pages/DiscussionsPage"));
const EcomOrderDetailPage = lazyWithRetry(() => import("@/features/ecom-orders/pages/EcomOrderDetailPage"));
const EcomOrdersPage = lazyWithRetry(() => import("@/features/ecom-orders/pages/EcomOrdersPage"));
const EmailSignaturePage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/email-signature/pages/EmailSignaturePage"));
const EmployeesPage = lazyWithRetry(() => import("@/features/employees/pages/EmployeesPage"));
const ExternalContactsPage = lazyWithRetry(() => import("@/features/external-contacts/pages/ExternalContactsPage"));
const FileManagerPage = lazyWithRetry(() => import("@/features/file-manager/pages/FileManagerPage"));
const GroupsPage = lazyWithRetry(() => import("@/features/groups/pages/GroupsPage"));
const GroupMembersPage = lazyWithRetry(() => import("@/features/groups/pages/GroupMembersPage"));
const DynamicGroupBuilderPage = lazyWithRetry(() => import("@/features/dynamic-groups/pages/DynamicGroupBuilderPage"));
const HelpdeskPage = lazyWithRetry(() => import("@/features/helpdesk/pages/HelpdeskPage"));
const HelpCenterLayout = lazyWithRetry(() => import("@/features/helpdesk/components/help-center/layout/HelpCenterLayout"));
const HelpCenterHomePage = lazyWithRetry(() => import("@/features/helpdesk/pages/HelpCenterHomePage"));
const HelpCenterCategoryPage = lazyWithRetry(() => import("@/features/helpdesk/pages/HelpCenterCategoryPage"));
const HelpCenterArticlePage = lazyWithRetry(() => import("@/features/helpdesk/pages/HelpCenterArticlePage"));
const ImpersonateGrantPage = lazyWithRetry(() => import("@/features/auth/pages/ImpersonateGrantPage"));
const InformationsPage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/informations/pages/InformationsPage"));
const InvoiceDetailPage = lazyWithRetry(() => import("@/features/activity-operations/pages/InvoiceDetailPage"));
const JobsPage = lazyWithRetry(() => import("@/features/jobs/pages/JobsPage"));
const LegalQuestionDetailPage = lazyWithRetry(() => import("@/features/legal-questions/pages/LegalQuestionDetailPage"));
const LegalQuestionsPage = lazyWithRetry(() => import("@/features/legal-questions/pages/LegalQuestionsPage"));
const LoginRedirect = lazyWithRetry(() => import("@/features/auth/pages/LoginRedirect"));
const LostPasswordPage = lazyWithRetry(() => import("@/features/auth/pages/LostPasswordPage"));
const MobileAppPage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/mobile-app/pages/MobileAppPage"));
const ModulesConfigPage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/modules-config/pages/ModulesConfigPage"));
const NavigationPositionPage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/theme-studio/pages/NavigationPositionPage"));
const NewPasswordPage = lazyWithRetry(() => import("@/features/auth/pages/NewPasswordPage"));
const NewsletterEditorPage = lazyWithRetry(() => import("@/features/communication/pages/NewsletterEditorPage").then((m) => ({ default: m.NewsletterEditorPage })));
const NewslettersPage = lazyWithRetry(() => import("@/features/communication/pages/NewslettersPage"));
const OpenQuestionsPage = lazyWithRetry(() => import("@/features/open-questions/pages/OpenQuestionsPage"));
const ParametersPage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/personnalisation/pages/ParametersPage"));
const PinnedNotesPage = lazyWithRetry(() => import("@/features/pinned-notes/pages/PinnedNotesPage"));
const PollResultsPage = lazyWithRetry(() => import("@/features/polls/pages/PollResultsPage"));
const PollsPage = lazyWithRetry(() => import("@/features/polls/pages/PollsPage"));
const SurveysPage = lazyWithRetry(() => import("@/features/surveys/pages/SurveysPage"));
const SurveyEditPage = lazyWithRetry(() => import("@/features/surveys/pages/SurveyEditPage"));
const SurveyDetailPage = lazyWithRetry(() => import("@/features/surveys/pages/SurveyDetailPage"));
const PronosticsCatalogPage = lazyWithRetry(() => import("@/features/pronostics/pages/PronosticsCatalogPage"));
const PronosticCompetitionPage = lazyWithRetry(() => import("@/features/pronostics/pages/PronosticCompetitionPage"));
const ProofDocumentTypesPage = lazyWithRetry(() => import("@/features/proof-documents/pages/ProofDocumentTypesPage"));
const ProofDocumentsPageSwitch = lazyWithRetry(() => import("@/features/proof-documents/pages/ProofDocumentsPageSwitch"));
const PushNotificationsPage = lazyWithRetry(() => import("@/features/push-notifications/pages/PushNotificationsPage"));
const RefundRequestDetailPage = lazyWithRetry(() => import("@/features/refund-requests/pages/RefundRequestDetailPage"));
const RefundRequestsPage = lazyWithRetry(() => import("@/features/refund-requests/pages/RefundRequestsPage"));
const RefundsConfigurationPage = lazyWithRetry(() => import("@/features/refunds/pages/RefundsConfigurationPage"));
const RepresentativeRolesPage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/representative-roles/pages/RepresentativeRolesPage"));
const RepresentativesDirectoryPage = lazyWithRetry(() => import("@/features/representatives/pages/RepresentativesDirectoryPage"));
const RepresentativesPage = lazyWithRetry(() => import("@/features/representatives/pages/RepresentativesPage"));
const RuleEditPage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/rules/components/RuleEditPage"));
const RulesPage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/rules/components"));
const SavedRepliesPage = lazyWithRetry(() => import("@/features/communication/pages/SavedRepliesPage"));
const SitesPage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/organization-sites/pages/SitesPage"));
const SmsSendsListPage = lazyWithRetry(() => import("@/features/sms/pages/SmsSendsListPage"));
const StudioLayout = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/theme-studio/components/StudioLayout"));
const SubsidyCreditsPage = lazyWithRetry(() => import("@/features/subsidy-credits/pages/SubsidyCreditsPage").then((m) => ({ default: m.SubsidyCreditsPage })));
const SubsidyLedgerPage = lazyWithRetry(() => import("@/features/subsidy-ledger/pages/SubsidyLedgerPage").then((m) => ({ default: m.SubsidyLedgerPage })));
const SubsidyPoliciesPage = lazyWithRetry(() => import("@/features/subsidy-policies/pages/SubsidyPoliciesPage"));
const SuppliersPage = lazyWithRetry(() => import("@/features/suppliers/pages/SuppliersPage"));
const TaskManagerPage = lazyWithRetry(() => import("@/features/task-manager/pages/TaskManagerPage"));
const ThemeDetailLayout = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/theme-studio/components/ThemeDetailLayout"));
const ThemePage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/theme-studio/pages/ThemePage"));
const ThemesPage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/theme-studio/pages/ThemesPage"));
const TombolaDashboardPage = lazyWithRetry(() => import("@/features/tombola/pages/TombolaDashboardPage"));
const TombolaPage = lazyWithRetry(() => import("@/features/tombola/pages/TombolaPage"));
const UserSettingsPage = lazyWithRetry(() => import("@/features/user-settings/pages/UserSettingsPage"));
const VisioConferencePage = lazyWithRetry(() => import("@/features/visio/pages/VisioConferencePage"));
const VisioRoomsPage = lazyWithRetry(() => import("@/features/visio/pages/VisioRoomsPage"));
const VoucherAndBalancePage = lazyWithRetry(() => import("@/features/voucher-and-balance/pages/VoucherAndBalancePage"));
const VoucherCampaignBeneficiariesPage = lazyWithRetry(() => import("@/features/voucher-campaigns/pages/VoucherCampaignBeneficiariesPage"));
const VoucherCampaignEmployeesPage = lazyWithRetry(() => import("@/features/voucher-campaigns/pages/VoucherCampaignEmployeesPage"));
const VoucherCampaignsListPage = lazyWithRetry(() => import("@/features/voucher-campaigns/pages/VoucherCampaignsListPage"));
const VoucherCampaignMccPage = lazyWithRetry(() => import("@/features/voucher-campaigns/pages/VoucherCampaignMccPage"));
const VouchersSettingsPage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/theme-studio/pages/VouchersSettingsPage"));
const WelcomeEmailEditorPage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/welcome-email/pages/WelcomeEmailEditorPage"));
const WelcomeEmailPage = lazyWithRetry(() => import("@/features/organization-settings/subFeatures/welcome-email/pages/WelcomeEmailPage"));
const YeatiLocBookingDetailPage = lazyWithRetry(() => import("@/features/yeati-loc/pages/YeatiLocBookingDetailPage"));
const YeatiLocCataloguePage = lazyWithRetry(() => import("@/features/yeati-loc/pages/YeatiLocCataloguePage"));
const YeatiLocCollectionsPage = lazyWithRetry(() => import("@/features/yeati-loc/pages/YeatiLocCollectionsPage"));
const YeatiLocDashboardPage = lazyWithRetry(() => import("@/features/yeati-loc/pages/YeatiLocDashboardPage"));
const YeatiLocPlanningPage = lazyWithRetry(() => import("@/features/yeati-loc/pages/YeatiLocPlanningPage"));
const YeatiLocProductDetailPage = lazyWithRetry(() => import("@/features/yeati-loc/pages/YeatiLocProductDetailPage"));
const YeatiLocReservationsPage = lazyWithRetry(() => import("@/features/yeati-loc/pages/YeatiLocReservationsPage"));
const YeatiLocSettingsPage = lazyWithRetry(() => import("@/features/yeati-loc/pages/YeatiLocSettingsPage"));
const YeatiLocWizardPage = lazyWithRetry(() => import("@/features/yeati-loc/pages/YeatiLocWizardPage"));
import {
  AUTO_LOGIN_ROUTE,
  IMPERSONATE_GRANT_ROUTE,
  IMPERSONATE_LOGIN_ROUTE,
  DYNAMIC_GROUP_NEW_PATH,
  DYNAMIC_GROUP_EDIT_PATH,
  VOUCHER_AND_BALANCE_TAB_VALUES,
  VOUCHER_ORDERS_PAGE_PATH,
  VOUCHER_EMPLOYEES_PATH,
  VOUCHER_TOP_UPS_PATH,
  VOUCHER_BALANCE_PATH,
  VOUCHER_CAMPAIGN_PATH,
  VOUCHER_CAMPAIGN_EMPLOYEES_PATH,
  VOUCHER_CAMPAIGN_BENEFICIARIES_PATH,
  VOUCHER_CAMPAIGN_MCC_PATH,
  EMPLOYEES_PAGE_PATH,
  EMPLOYEE_TRANSFER_PAGE_PATH,
  BENEFICIARIES_PAGE_PATH,
  CHECK_TAB_VALUES,
  CHECK_CAMPAIGNS_PATH,
  CHECK_CAMPAIGN_EMPLOYEES_PATH,
  CHECK_CAMPAIGN_BENEFICIARIES_PATH,
  CHECK_EMPLOYEES_PATH,
  CHECK_CONTRIBUTIONS_PATH,
  CARD_REISSUANCE_PATH,
  REPRESENTATIVES_PAGE_PATH,
  REPRESENTATIVES_DIRECTORY_PAGE_PATH,
  EXTERNAL_CONTACTS_PAGE_PATH,
  SUPPLIERS_PAGE_PATH,
  JOBS_PAGE_PATH,
  CARD_REISSUANCE_ORDER_PATH,
  PARAMETER_PATH,
  USER_SETTINGS_PATH,
  USER_LINK_ACCOUNTS_PATH,
  GROUPS_PAGE_PATH,
  GROUP_NEW_PATH,
  GROUP_EDIT_PATH,
  POLLS_PAGE_PATH,
  POLL_RESULTS_PAGE_PATH,
  SURVEYS_PAGE_PATH,
  SURVEY_CREATE_PATH,
  SURVEY_EDIT_PATH,
  SURVEY_DETAIL_PATH,
  PINNED_NOTES_PATH,
  DISCUSSIONS_PAGE_PATH,
  CHAT_PAGE_PATH,
  AUDIT_LOGS_PAGE_PATH,
  FILE_MANAGER_PATH,
  FILE_MANAGER_FOLDER_PATH,
  BLOG_PAGE_PATH,
  BLOG_NEW_ARTICLE_PATH,
  BLOG_ARTICLE_PATH,
  BLOG_EDIT_ARTICLE_PAGE_PATH,
  PRONOSTICS_PAGE_PATH,
  PRONOSTIC_COMPETITION_DETAIL_PATH,
  TOMBOLA_PAGE_PATH,
  TOMBOLA_NEW_PATH,
  TOMBOLA_DASHBOARD_PATH,
  ADVENT_CALENDAR_PAGE_PATH,
  ADVENT_CALENDAR_DASHBOARD_PATH,
  ARCADE_PAGE_PATH,
  ARCADE_GAME_PATH,
  ARCADE_COMPETITION_PATH,
  CALENDAR_PATH,
  PROOF_DOCUMENTS_PAGE_PATH,
  ACCOUNTING_MOVEMENTS_PAGE_PATH,
  BRIDGE_CALLBACK_PAGE_PATH,
  LEGACY_ACCOUNTING_MOVEMENTS_PAGE_PATH,
  ACCOUNTING_SEPA_REMITTANCES_PAGE_PATH,
  ACCOUNTING_SEPA_HELP_PAGE_PATH,
  ACCOUNTING_SETTLEMENTS_PAGE_PATH,
  LEGACY_ACCOUNTING_SETTLEMENTS_PAGE_PATH,
  ACCOUNTING_LEDGER_PAGE_PATH,
  ACCOUNTING_STOCK_PAGE_PATH,
  ACCOUNTING_SETTINGS_PAGE_PATH,
  ACCOUNTING_SETTINGS_FISCAL_YEAR_PATH,
  ACCOUNTING_SETTINGS_BANK_ACCOUNTS_PATH,
  ACCOUNTING_SETTINGS_BRIDGE_PATH,
  ACCOUNTING_SETTINGS_PAYBOX_PATH,
  ACCOUNTING_SETTINGS_TAGS_PATH,
  ACCOUNTING_SETTINGS_CATEGORIES_PATH,
  ACCOUNTING_BUDGET_HELP_PAGE_PATH,
  ACCOUNTING_SETTINGS_HELP_PAGE_PATH,
  ACCOUNTING_BRIDGE_HELP_PAGE_PATH,
  ACCOUNTING_MOVEMENT_HELP_PAGE_PATH,
  ACCOUNTING_SETTLEMENTS_HELP_PAGE_PATH,
  ACCOUNTING_FUSION_HELP_PAGE_PATH,
  ACCOUNTING_SYNTHESIS_PAGE_PATH,
  ACCOUNTING_BUDGET_PAGE_PATH,
  RULE_NEW_PATH,
  RULE_EDIT_PATH,
  BATCH_RULE_EXECUTION_PATH,
  CUSTOM_FORMS_PAGE_PATH,
  CUSTOM_FORM_NEW_PATH,
  CUSTOM_FORM_EDIT_PATH,
  CUSTOM_FORM_SUBMISSIONS_PATH,
  TASK_MANAGER_PAGE_PATH,
  VISIO_PAGE_PATH,
  VISIO_PUBLIC_PATH,
  VISIO_ROOM_PATH,
  VISIO_PUBLIC_STANDALONE_PATH,
  VISIO_ROOM_STANDALONE_PATH,
  PUSH_NOTIFICATIONS_PAGE_PATH,
  OPEN_QUESTIONS_PAGE_PATH,
  LEGAL_QUESTIONS_PAGE_PATH,
  LEGAL_QUESTION_DETAIL_PATH,
  SMS_ADMIN_PAGE_PATH,
  COMMUNICATION_DISCUSSIONS_PATH,
  COMMUNICATION_DISCUSSION_DETAIL_PATH,
  COMMUNICATION_NEWSLETTERS_PATH,
  COMMUNICATION_NEWSLETTER_NEW_PATH,
  COMMUNICATION_NEWSLETTER_EDITOR_PATH,
  COMMUNICATION_CAMPAIGN_REPORT_DETAIL_PATH,
  COMMUNICATION_REPLIES_PATH,
  COMMUNICATION_CSE_MESSAGES_PATH,
  COMMUNICATION_CSE_MESSAGE_NEW_PATH,
  COMMUNICATION_CSE_MESSAGE_EDITOR_PATH,
  SUBSIDY_POLICIES_PAGE_PATH,
  SUBSIDY_LEDGER_PAGE_PATH,
  SUBSIDY_CREDITS_PAGE_PATH,
  ACTIVITY_OPERATIONS_PAGE_PATH,
  LEGACY_ACTIVITY_OPERATIONS_PAGE_PATH,
  INVOICE_DETAIL_PATH,
  DEALS_PAGE_PATH,
  DEALS_CATALOGUE_GENRES_PATH,
  DEALS_CATALOGUE_FAMILIES_PATH,
  DEALS_CATALOGUE_REFERENCES_PATH,
  DEALS_CATALOGUE_PARTNER_OFFERS_PATH,
  DELIVERY_MODES_PAGE_PATH,
  CHANGELOGS_PAGE_PATH,
  REFUNDS_CONFIGURATION_PATH,
  HELP_CENTER_ARTICLE_PATH,
  HELP_CENTER_CATEGORY_PATH,
  HELP_CENTER_PATH,
  HELPDESK_TICKETS_PATH,
  ECOM_ORDERS_PAGE_PATH,
  ECOM_ORDER_DETAIL_PATH,
  REFUND_REQUESTS_PAGE_PATH,
  REFUND_REQUEST_DETAIL_PATH,
  REFUNDS_HELP_PAGE_PATH,
  YEATI_LOC_PATH,
  YEATI_LOC_CATALOGUE_PATH,
  YEATI_LOC_NEW_PRODUCT_PATH,
  YEATI_LOC_PRODUCT_DETAIL_PATH,
  YEATI_LOC_RESERVATIONS_PATH,
  YEATI_LOC_BOOKING_DETAIL_PATH,
  YEATI_LOC_PLANNING_PATH,
  YEATI_LOC_COLLECTIONS_PATH,
  YEATI_LOC_SETTINGS_PATH,
  YEATI_LOC_HELP_PAGE_PATH,
  CLASSIFIED_ADS_PATH,
} from "@/shared/configs/routes.config";
import { useOrganizationPlan } from "@/shared/hooks/useOrganizationFeatures";
import { isDashboardCommunicationPlan } from "@/shared/utils/dashboardCommunicationPlan";
import { lazyWithRetry } from "@/shared/utils/lazyWithRetry";
import TrackedRoutes from "./usage-tracking/TrackedRoutes";

const DashboardRouter = () => {
  const { plan, isLoading } = useOrganizationPlan();

  if (isLoading) return null;

  return (
    <>
      {isDashboardCommunicationPlan(plan?.code)
        ? <DashboardCommunicationPage />
        : (
          <>
            <CampaignTeaserVisit />
            <DashboardPage />
          </>
        )}
    </>
  );
};

const AppRouter = () => {
  return (
    <TrackedRoutes>
      {/* ----- ROUTES WITHOUT SIDEBAR ----- */}
      <Route element={<LayoutWithoutSidebar />}>
        <Route path="/login" element={<LoginRedirect />} />
        <Route
          path="/mot-de-passe-oublie"
          element={<LostPasswordPage title="Mot de passe oublié" />}
        />
        <Route
          path="/reconfig"
          element={<NewPasswordPage title="Nouveau mot de passe" />}
        />
        <Route path={IMPERSONATE_LOGIN_ROUTE} element={<LoginRedirect />} />
        <Route path={IMPERSONATE_GRANT_ROUTE} element={<ImpersonateGrantPage />} />
        <Route path={AUTO_LOGIN_ROUTE} element={<CrossScopeAutoLoginPage />} />
        <Route path="*" element={<RouteNotFoundPage />} />
      </Route>

      {/* ----- ROUTES WITH SIDEBAR ----- */}
      <Route element={<CampaignVisitBoundary><PrivateRoute /></CampaignVisitBoundary>}>
        <Route element={<Layout />}>
          <Route index element={<DashboardRouter />} />
          <Route
            path={REPRESENTATIVES_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <RepresentativesPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={REPRESENTATIVES_DIRECTORY_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <RepresentativesDirectoryPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={EMPLOYEES_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <EmployeesPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={EMPLOYEE_TRANSFER_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <Navigate to="/salaries?tab=transferts" replace />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={BATCH_RULE_EXECUTION_PATH}
            element={(
              <FeatureProtectedRoute>
                <BatchRuleExecutionPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={BENEFICIARIES_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <BeneficiariesPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={PROOF_DOCUMENTS_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <ProofDocumentsPageSwitch />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={ACCOUNTING_MOVEMENTS_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <RemountOnFilterView><AccountingMovementsPage /></RemountOnFilterView>
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={BRIDGE_CALLBACK_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <BridgeCallbackPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={LEGACY_ACCOUNTING_MOVEMENTS_PAGE_PATH}
            element={<Navigate to={ACCOUNTING_MOVEMENTS_PAGE_PATH} replace />}
          />
          <Route
            path={ACCOUNTING_SETTLEMENTS_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <RemountOnFilterView><AccountingSettlementsPage /></RemountOnFilterView>
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={LEGACY_ACCOUNTING_SETTLEMENTS_PAGE_PATH}
            element={<Navigate to={ACCOUNTING_SETTLEMENTS_PAGE_PATH} replace />}
          />
          <Route
            path={ACCOUNTING_SEPA_REMITTANCES_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <AccountingSepaRemittancesPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={ACCOUNTING_SEPA_HELP_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <HelpPage
                  slug="compta/remises-sepa"
                  title="Guide : payer les salariés par virement SEPA"
                  description="Préparer une remise, l'envoyer à la banque et suivre chaque paiement, pas à pas."
                  backPath={ACCOUNTING_SEPA_REMITTANCES_PAGE_PATH}
                  backPathLegend="Remises de virements"
                />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={ACCOUNTING_SYNTHESIS_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <RemountOnFilterView><AccountingSynthesisPage /></RemountOnFilterView>
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={ACCOUNTING_LEDGER_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <RemountOnFilterView><AccountingLedgerPage /></RemountOnFilterView>
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={ACCOUNTING_STOCK_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <RemountOnFilterView><AccountingLedgerPage stockMode /></RemountOnFilterView>
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={ACCOUNTING_BUDGET_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <AccountingBudgetPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={ACCOUNTING_SETTLEMENTS_HELP_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <HelpPage
                  slug="compta/remises-bancaires"
                  title="Guide : créer une remise bancaire"
                  description="Enregistrer les chèques et paiements par carte reçus, créer la remise et la rapprocher du relevé, pas à pas."
                  backPath={ACCOUNTING_SETTLEMENTS_PAGE_PATH}
                  backPathLegend="Remises bancaires"
                />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={ACCOUNTING_MOVEMENT_HELP_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <HelpPage
                  slug="compta/mouvement-bancaire"
                  title="Guide : créer et justifier un mouvement bancaire"
                  description="Saisir une opération du relevé, puis l'affecter à la bonne catégorie, pas à pas."
                  backPath={ACCOUNTING_MOVEMENTS_PAGE_PATH}
                  backPathLegend="Flux financiers"
                />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={ACCOUNTING_FUSION_HELP_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <HelpPage
                  slug="compta/fusion-bancaire"
                  title="Guide : fusionner une saisie avec la ligne du relevé"
                  description="Pourquoi rapprocher une écriture saisie de la ligne importée par la banque, et comment Yeati la retrouve."
                  backPath={ACCOUNTING_MOVEMENTS_PAGE_PATH}
                  backPathLegend="Flux financiers"
                />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={ACCOUNTING_BUDGET_HELP_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <HelpPage
                  slug="compta/budgets"
                  title="Guide : fixer les budgets du CSE"
                  description="Poser l'enveloppe de l'exercice, la répartir par catégorie et suivre le réalisé, pas à pas."
                  backPath={ACCOUNTING_BUDGET_PAGE_PATH}
                  backPathLegend="Budgets"
                />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={ACCOUNTING_SETTINGS_HELP_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <HelpPage
                  slug="compta/parametres"
                  title="Guide : paramétrer la comptabilité"
                  description="Régler l'exercice comptable et déclarer les comptes bancaires du CSE, pas à pas."
                  backPath={ACCOUNTING_SETTINGS_FISCAL_YEAR_PATH}
                  backPathLegend="Réglages comptabilité"
                />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={ACCOUNTING_BRIDGE_HELP_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <HelpPage
                  slug="compta/synchronisation-bancaire"
                  title="Guide : synchroniser ses comptes bancaires"
                  description="Connecter la banque du CSE avec Bridge et suivre l'import des mouvements, pas à pas."
                  backPath={ACCOUNTING_SETTINGS_BRIDGE_PATH}
                  backPathLegend="Synchronisation bancaire"
                />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={ACCOUNTING_SETTINGS_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <AccountingSettingsLayout />
              </FeatureProtectedRoute>
            )}
          >
            <Route index element={<Navigate to={ACCOUNTING_SETTINGS_FISCAL_YEAR_PATH} replace />} />
            <Route path={ACCOUNTING_SETTINGS_FISCAL_YEAR_PATH} element={<AccountingFiscalYearPage />} />
            <Route path={ACCOUNTING_SETTINGS_BANK_ACCOUNTS_PATH} element={<AccountingBankAccountsPage />} />
            <Route path={ACCOUNTING_SETTINGS_BRIDGE_PATH} element={<AccountingBridgePage />} />
            <Route path={ACCOUNTING_SETTINGS_PAYBOX_PATH} element={<AccountingPayboxPage />} />
            <Route path={ACCOUNTING_SETTINGS_CATEGORIES_PATH} element={<AccountingCategoriesPage />} />
            <Route path={ACCOUNTING_SETTINGS_TAGS_PATH} element={<AccountingTagsPage />} />
          </Route>
          <Route
            path={SUBSIDY_POLICIES_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <SubsidyPoliciesPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={SUBSIDY_LEDGER_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <SubsidyLedgerPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={SUBSIDY_CREDITS_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <SubsidyCreditsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={ACTIVITY_OPERATIONS_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <ActivityOperationsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={INVOICE_DETAIL_PATH}
            element={(
              <FeatureProtectedRoute>
                <InvoiceDetailPage />
              </FeatureProtectedRoute>
            )}
          />
          {/* Activités déplacée vers la Comptabilité : redirige l'ancien path. */}
          <Route
            path={LEGACY_ACTIVITY_OPERATIONS_PAGE_PATH}
            element={<Navigate to={ACTIVITY_OPERATIONS_PAGE_PATH} replace />}
          />
          <Route
            path={EXTERNAL_CONTACTS_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <ExternalContactsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={GROUPS_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <GroupsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={GROUP_NEW_PATH}
            element={(
              <FeatureProtectedRoute>
                <GroupMembersPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={GROUP_EDIT_PATH}
            element={(
              <FeatureProtectedRoute>
                <GroupMembersPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={DYNAMIC_GROUP_NEW_PATH}
            element={(
              <FeatureProtectedRoute>
                <DynamicGroupBuilderPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={DYNAMIC_GROUP_EDIT_PATH}
            element={(
              <FeatureProtectedRoute>
                <DynamicGroupBuilderPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route path={SUPPLIERS_PAGE_PATH} element={<SuppliersPage />} />
          <Route
            path={DEALS_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <DealsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={DEALS_CATALOGUE_GENRES_PATH}
            element={(
              <FeatureProtectedRoute>
                <DealsPage defaultTab="genres" />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={DEALS_CATALOGUE_FAMILIES_PATH}
            element={(
              <FeatureProtectedRoute>
                <DealsPage defaultTab="families" />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={DEALS_CATALOGUE_REFERENCES_PATH}
            element={(
              <FeatureProtectedRoute>
                <DealsPage defaultTab="references" />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={DEALS_CATALOGUE_PARTNER_OFFERS_PATH}
            element={(
              <FeatureProtectedRoute>
                <DealsPage defaultTab="partner-offers" />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={DELIVERY_MODES_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <DeliveryModesPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={CLASSIFIED_ADS_PATH}
            element={(
              <FeatureProtectedRoute>
                <ClassifiedAdsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={POLLS_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <PollsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={POLL_RESULTS_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <PollResultsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={SURVEYS_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <SurveysPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={SURVEY_CREATE_PATH}
            element={(
              <FeatureProtectedRoute>
                <SurveyEditPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={SURVEY_EDIT_PATH}
            element={(
              <FeatureProtectedRoute>
                <SurveyEditPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={SURVEY_DETAIL_PATH}
            element={(
              <FeatureProtectedRoute>
                <SurveyDetailPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={BLOG_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <BlogPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={PRONOSTICS_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <PronosticsCatalogPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={PRONOSTIC_COMPETITION_DETAIL_PATH}
            element={(
              <FeatureProtectedRoute>
                <PronosticCompetitionPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={TOMBOLA_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <TombolaPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={TOMBOLA_NEW_PATH}
            element={(
              <FeatureProtectedRoute>
                <TombolaDashboardPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={TOMBOLA_DASHBOARD_PATH}
            element={(
              <FeatureProtectedRoute>
                <TombolaDashboardPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={ADVENT_CALENDAR_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <AdventCalendarPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={ADVENT_CALENDAR_DASHBOARD_PATH}
            element={(
              <FeatureProtectedRoute>
                <AdventCalendarDashboardPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={ARCADE_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <ArcadePage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={ARCADE_GAME_PATH}
            element={(
              <FeatureProtectedRoute>
                <ArcadeInstancePage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={ARCADE_COMPETITION_PATH}
            element={(
              <FeatureProtectedRoute>
                <ArcadeCompetitionPage />
              </FeatureProtectedRoute>
            )}
          />
          {/* Yeati Arena was renamed Yeati Arcade: keep old bookmarks working */}
          <Route path="/arena/*" element={<Navigate to={ARCADE_PAGE_PATH} replace />} />
          <Route
            path={BLOG_ARTICLE_PATH}
            element={(
              <FeatureProtectedRoute>
                <ArticlePage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={BLOG_NEW_ARTICLE_PATH}
            element={(
              <FeatureProtectedRoute>
                <CreateOrUpdateArticlePage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={BLOG_EDIT_ARTICLE_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <CreateOrUpdateArticlePage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={CALENDAR_PATH}
            element={(
              <FeatureProtectedRoute>
                <CalendarPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={VOUCHER_CAMPAIGN_PATH}
            element={(
              <FeatureProtectedRoute>
                <VoucherCampaignsListPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={VOUCHER_CAMPAIGN_EMPLOYEES_PATH}
            element={(
              <FeatureProtectedRoute>
                <VoucherCampaignEmployeesPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={VOUCHER_CAMPAIGN_BENEFICIARIES_PATH}
            element={(
              <FeatureProtectedRoute>
                <VoucherCampaignBeneficiariesPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={VOUCHER_CAMPAIGN_MCC_PATH}
            element={(
              <FeatureProtectedRoute>
                <VoucherCampaignMccPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={VOUCHER_ORDERS_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <VoucherAndBalancePage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={VOUCHER_EMPLOYEES_PATH}
            element={(
              <FeatureProtectedRoute>
                <VoucherAndBalancePage
                  defaultTab={VOUCHER_AND_BALANCE_TAB_VALUES[0]}
                />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={VOUCHER_TOP_UPS_PATH}
            element={(
              <FeatureProtectedRoute>
                <VoucherAndBalancePage
                  defaultTab={VOUCHER_AND_BALANCE_TAB_VALUES[1]}
                />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={`${VOUCHER_BALANCE_PATH}/:id?`}
            element={(
              <FeatureProtectedRoute>
                <VoucherAndBalancePage
                  defaultTab={VOUCHER_AND_BALANCE_TAB_VALUES[2]}
                />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={CARD_REISSUANCE_PATH}
            element={(
              <FeatureProtectedRoute>
                <CardReissuancesPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={CARD_REISSUANCE_ORDER_PATH}
            element={(
              <FeatureProtectedRoute>
                <CardReissuancesOrderPage />
              </FeatureProtectedRoute>
            )}
          />
          {/* Cartes cadeaux (checks) routes */}
          <Route
            path={CHECK_CAMPAIGNS_PATH}
            element={(
              <FeatureProtectedRoute>
                <ChecksPage defaultTab={CHECK_TAB_VALUES[0]} />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={CHECK_CAMPAIGN_EMPLOYEES_PATH}
            element={(
              <FeatureProtectedRoute>
                <CheckCampaignEmployeesPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={CHECK_CAMPAIGN_BENEFICIARIES_PATH}
            element={(
              <FeatureProtectedRoute>
                <CheckCampaignBeneficiariesPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={CHECK_EMPLOYEES_PATH}
            element={(
              <FeatureProtectedRoute>
                <ChecksPage defaultTab={CHECK_TAB_VALUES[1]} />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={CHECK_CONTRIBUTIONS_PATH}
            element={(
              <FeatureProtectedRoute>
                <ChecksPage defaultTab={CHECK_TAB_VALUES[2]} />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={JOBS_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <JobsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={OPEN_QUESTIONS_PAGE_PATH}
            element={<FeatureProtectedRoute><OpenQuestionsPage /></FeatureProtectedRoute>}
          />
          <Route
            path={LEGAL_QUESTIONS_PAGE_PATH}
            element={<FeatureProtectedRoute><LegalQuestionsPage /></FeatureProtectedRoute>}
          />
          <Route
            path={LEGAL_QUESTION_DETAIL_PATH}
            element={<FeatureProtectedRoute><LegalQuestionDetailPage /></FeatureProtectedRoute>}
          />
          <Route
            path={PINNED_NOTES_PATH}
            element={(
              <FeatureProtectedRoute>
                <PinnedNotesPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={TASK_MANAGER_PAGE_PATH}
            element={<FeatureProtectedRoute><TaskManagerPage /></FeatureProtectedRoute>}
          />
          <Route
            path={DISCUSSIONS_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <DiscussionsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={CHAT_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <ChatPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={`${CHAT_PAGE_PATH}/:roomId`}
            element={(
              <FeatureProtectedRoute>
                <ChatPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={AUDIT_LOGS_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <AuditLogsPage />
              </FeatureProtectedRoute>
            )}
          />
          {/* Help center & helpdesk routes */}
          <Route element={<HelpCenterLayout />}>
            <Route path={HELP_CENTER_PATH} element={<HelpCenterHomePage />} />
            <Route path={HELP_CENTER_CATEGORY_PATH} element={<HelpCenterCategoryPage />} />
            <Route path={HELP_CENTER_ARTICLE_PATH} element={<HelpCenterArticlePage />} />
          </Route>
          <Route path={HELPDESK_TICKETS_PATH} element={<HelpdeskPage />} />
          {/* Refund requests routes */}
          <Route
            path={REFUND_REQUESTS_PAGE_PATH}
            element={<FeatureProtectedRoute><RefundRequestsPage /></FeatureProtectedRoute>}
          />
          <Route
            path={REFUNDS_HELP_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <HelpPage
                  slug="remboursements/demandes"
                  title="Guide : proposer et traiter les remboursements"
                  description="Paramétrer les familles et références, puis étudier, accepter et payer les demandes des salariés, pas à pas."
                  backPath={REFUND_REQUESTS_PAGE_PATH}
                  backPathLegend="Remboursements"
                />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={REFUND_REQUEST_DETAIL_PATH}
            element={<FeatureProtectedRoute><RefundRequestDetailPage /></FeatureProtectedRoute>}
          />
          {/* Communication routes */}
          <Route
            path={COMMUNICATION_DISCUSSIONS_PATH}
            element={<FeatureProtectedRoute><CommunicationPage /></FeatureProtectedRoute>}
          />
          <Route
            path={COMMUNICATION_DISCUSSION_DETAIL_PATH}
            element={<FeatureProtectedRoute><CommunicationPage /></FeatureProtectedRoute>}
          />
          <Route
            path={COMMUNICATION_NEWSLETTERS_PATH}
            element={<FeatureProtectedRoute><NewslettersPage /></FeatureProtectedRoute>}
          />
          <Route
            path={COMMUNICATION_NEWSLETTER_NEW_PATH}
            element={<FeatureProtectedRoute><NewsletterEditorPage /></FeatureProtectedRoute>}
          />
          <Route
            path={COMMUNICATION_NEWSLETTER_EDITOR_PATH}
            element={<FeatureProtectedRoute><NewsletterEditorPage /></FeatureProtectedRoute>}
          />
          <Route
            path={COMMUNICATION_CAMPAIGN_REPORT_DETAIL_PATH}
            element={<FeatureProtectedRoute><CampaignReportDetailPage /></FeatureProtectedRoute>}
          />
          <Route
            path={COMMUNICATION_REPLIES_PATH}
            element={<FeatureProtectedRoute><SavedRepliesPage /></FeatureProtectedRoute>}
          />
          <Route
            path={COMMUNICATION_CSE_MESSAGES_PATH}
            element={<FeatureProtectedRoute><CseMessagesPage /></FeatureProtectedRoute>}
          />
          <Route
            path={COMMUNICATION_CSE_MESSAGE_NEW_PATH}
            element={<FeatureProtectedRoute><CseMessageWizardPage /></FeatureProtectedRoute>}
          />
          <Route
            path={COMMUNICATION_CSE_MESSAGE_EDITOR_PATH}
            element={<FeatureProtectedRoute><CseMessageWizardPage /></FeatureProtectedRoute>}
          />
          {/* Refunds routes */}
          <Route
            path={REFUNDS_CONFIGURATION_PATH}
            element={<FeatureProtectedRoute><RefundsConfigurationPage /></FeatureProtectedRoute>}
          />
          {/* Visio routes */}
          <Route
            path={VISIO_PAGE_PATH}
            element={<FeatureProtectedRoute><VisioRoomsPage /></FeatureProtectedRoute>}
          />
          <Route
            path={VISIO_PUBLIC_PATH}
            element={<FeatureProtectedRoute><VisioConferencePage /></FeatureProtectedRoute>}
          />
          <Route
            path={VISIO_ROOM_PATH}
            element={<FeatureProtectedRoute><VisioConferencePage /></FeatureProtectedRoute>}
          />
          <Route
            path={PUSH_NOTIFICATIONS_PAGE_PATH}
            element={<FeatureProtectedRoute><PushNotificationsPage /></FeatureProtectedRoute>}
          />
          <Route
            path={SMS_ADMIN_PAGE_PATH}
            element={<FeatureProtectedRoute><SmsSendsListPage /></FeatureProtectedRoute>}
          />
          <Route
            path={CHANGELOGS_PAGE_PATH}
            element={<FeatureProtectedRoute><ChangelogsPage /></FeatureProtectedRoute>}
          />
          {/* Ecom orders routes */}
          <Route
            path={ECOM_ORDERS_PAGE_PATH}
            element={<FeatureProtectedRoute><EcomOrdersPage /></FeatureProtectedRoute>}
          />
          <Route
            path={ECOM_ORDER_DETAIL_PATH}
            element={<FeatureProtectedRoute><EcomOrderDetailPage /></FeatureProtectedRoute>}
          />

          {/* Editor route at Layout level — no ParametersLayout wrapper */}
          <Route
            path={`${PARAMETER_PATH}/tableaux-de-bord/:id/edit`}
            element={(
              <FeatureProtectedRoute>
                <DashboardLayoutEditorPage />
              </FeatureProtectedRoute>
            )}
          />

          <Route
            path={FILE_MANAGER_PATH}
            element={(
              <FeatureProtectedRoute>
                <FileManagerPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={FILE_MANAGER_FOLDER_PATH}
            element={(
              <FeatureProtectedRoute>
                <FileManagerPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={USER_SETTINGS_PATH}
            element={(
              <FeatureProtectedRoute>
                <UserSettingsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={`${USER_SETTINGS_PATH}/notifications`}
            element={(
              <FeatureProtectedRoute>
                <UserSettingsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={`${USER_SETTINGS_PATH}/avatar`}
            element={(
              <FeatureProtectedRoute>
                <UserSettingsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={`${USER_SETTINGS_PATH}/filtres`}
            element={(
              <FeatureProtectedRoute>
                <UserSettingsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={USER_LINK_ACCOUNTS_PATH}
            element={(
              <FeatureProtectedRoute>
                <UserSettingsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={`${USER_SETTINGS_PATH}/sessions`}
            element={(
              <FeatureProtectedRoute>
                <UserSettingsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={`${USER_SETTINGS_PATH}/signature`}
            element={(
              <FeatureProtectedRoute>
                <UserSettingsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route path={RULE_NEW_PATH} element={<RuleEditPage />} />
          <Route path={RULE_EDIT_PATH} element={<RuleEditPage />} />
          <Route path={CUSTOM_FORMS_PAGE_PATH} element={<FeatureProtectedRoute><CustomFormsPage /></FeatureProtectedRoute>} />
          <Route path={CUSTOM_FORM_NEW_PATH} element={<FeatureProtectedRoute><CustomFormEditPage /></FeatureProtectedRoute>} />
          <Route
            path={CUSTOM_FORM_EDIT_PATH}
            element={<FeatureProtectedRoute><CustomFormEditPage /></FeatureProtectedRoute>}
          />
          <Route
            path={CUSTOM_FORM_SUBMISSIONS_PATH}
            element={<FeatureProtectedRoute><CustomFormSubmissionsPage /></FeatureProtectedRoute>}
          />
          {/* Yeati Loc routes — toutes sous le Layout normal (scroll OK).
              Auparavant booking_detail / planning / collections /
              settings n'étaient déclarés que dans le 2e bloc
              `<Layout isOrganizationSettingsLayout />` plus bas, ce qui
              appliquait `lg:overflow-hidden` sur le SidebarInset et
              bloquait le scroll sur ces pages. Toutes regroupées ici
              pour partager le même Layout standard. */}
          <Route path={YEATI_LOC_PATH} element={<FeatureProtectedRoute><YeatiLocDashboardPage /></FeatureProtectedRoute>} />
          <Route path={YEATI_LOC_CATALOGUE_PATH} element={<FeatureProtectedRoute><YeatiLocCataloguePage /></FeatureProtectedRoute>} />
          <Route path={YEATI_LOC_NEW_PRODUCT_PATH} element={<FeatureProtectedRoute><YeatiLocWizardPage /></FeatureProtectedRoute>} />
          <Route
            path={YEATI_LOC_HELP_PAGE_PATH}
            element={(
              <FeatureProtectedRoute>
                <HelpPage
                  slug="locations/parametrage"
                  title="Guide : paramétrer les locations et leurs formulaires de réservation"
                  description="Collections et quotas par saison, hébergements, séjours et tarifs, formulaires, voyages et reprise d'une saison sur l'autre, pas à pas."
                  backPath={YEATI_LOC_CATALOGUE_PATH}
                  backPathLegend="Catalogue"
                />
              </FeatureProtectedRoute>
            )}
          />
          <Route path={YEATI_LOC_PRODUCT_DETAIL_PATH} element={<FeatureProtectedRoute><YeatiLocProductDetailPage /></FeatureProtectedRoute>} />
          <Route path={YEATI_LOC_RESERVATIONS_PATH} element={<FeatureProtectedRoute><YeatiLocReservationsPage /></FeatureProtectedRoute>} />
          <Route path={YEATI_LOC_BOOKING_DETAIL_PATH} element={<FeatureProtectedRoute><YeatiLocBookingDetailPage /></FeatureProtectedRoute>} />
          <Route path={YEATI_LOC_PLANNING_PATH} element={<FeatureProtectedRoute><YeatiLocPlanningPage /></FeatureProtectedRoute>} />
          <Route path={YEATI_LOC_COLLECTIONS_PATH} element={<FeatureProtectedRoute><YeatiLocCollectionsPage /></FeatureProtectedRoute>} />
          <Route path={YEATI_LOC_SETTINGS_PATH} element={<FeatureProtectedRoute><YeatiLocSettingsPage /></FeatureProtectedRoute>} />
        </Route>

        {/* Pages opened in their own tab, without the console layout */}
        <Route element={<StandaloneLayout />}>
          <Route
            path={VISIO_PUBLIC_STANDALONE_PATH}
            element={<FeatureProtectedRoute><VisioConferencePage standalone /></FeatureProtectedRoute>}
          />
          <Route
            path={VISIO_ROOM_STANDALONE_PATH}
            element={<FeatureProtectedRoute><VisioConferencePage standalone /></FeatureProtectedRoute>}
          />
        </Route>

        {/* Parameters routes with shared layout */}
        <Route element={<Layout isOrganizationSettingsLayout />}>
          <Route path={PARAMETER_PATH} element={<ParametersLayout />}>
            <Route index element={<ParametersIndexRedirect />} />
            {/* Identité du CSE - accessible à tous les CSE */}
            <Route path="identite-du-cse" element={<ParametersPage />} />
            {/* Studio nested routes - feature-flagged behind serviceTheme */}
            <Route path="studio" element={<FeatureProtectedRoute><StudioLayout /></FeatureProtectedRoute>}>
              <Route index element={<Navigate to="themes" replace />} />
              <Route
                path="themes"
                element={<FeatureProtectedRoute><ThemesPage /></FeatureProtectedRoute>}
              />
              <Route
                path="themes/:themeId"
                element={<FeatureProtectedRoute><ThemeDetailLayout /></FeatureProtectedRoute>}
              >
                <Route
                  path="couleurs"
                  element={<ThemePage />}
                />
                <Route
                  path="bannieres"
                  element={<BannersPage />}
                />
                <Route
                  path="bons"
                  element={<VouchersSettingsPage />}
                />
                <Route
                  path="tableaux-de-bord"
                  element={<DashboardLayoutsPage />}
                />
                <Route
                  path="menu"
                  element={<NavigationPositionPage />}
                />
              </Route>
              <Route path="application-mobile" element={<MobileAppPage />} />
              {/* Redirect old personnalisation to new standalone page */}
              <Route path="personnalisation" element={<Navigate to="/parametres/identite-du-cse" replace />} />
              {/* Redirects for old direct paths */}
              <Route path="couleurs" element={<Navigate to="themes" replace />} />
              <Route path="bannieres" element={<Navigate to="themes" replace />} />
              <Route path="bons" element={<Navigate to="themes" replace />} />
              <Route path="tableaux-de-bord" element={<Navigate to="themes" replace />} />
              <Route path="menu" element={<Navigate to="themes" replace />} />
              {/* Redirect old path */}
              <Route path="position-menu" element={<Navigate to="menu" replace />} />
              <Route path="theme" element={<Navigate to="themes" replace />} />
            </Route>
            {/* Redirects from old paths */}
            <Route path="personnalisation" element={<Navigate to="identite-du-cse" replace />} />
            <Route path="tableaux-de-bord" element={<Navigate to="studio/themes" replace />} />
            <Route path="position-menu" element={<Navigate to="studio/themes" replace />} />
            <Route path="menu" element={<Navigate to="studio/themes" replace />} />
            <Route path="theme" element={<Navigate to="studio/themes" replace />} />
            <Route path="bannieres" element={<Navigate to="studio/themes" replace />} />
            <Route path="bons" element={<Navigate to="studio/themes" replace />} />
            {/* Non-studio parameter routes */}
            <Route path="coordonnees" element={<FeatureProtectedRoute><CoordinatesPage /></FeatureProtectedRoute>} />
            <Route path="factures" element={<SubscriptionInvoicesPage />} />
            <Route path="prelevement" element={<DirectDebitPage />} />
            <Route path="informations" element={<FeatureProtectedRoute><InformationsPage /></FeatureProtectedRoute>} />
            <Route path="gestion-des-sites" element={<FeatureProtectedRoute><SitesPage /></FeatureProtectedRoute>} />
            <Route path="email-bienvenue" element={<FeatureProtectedRoute><WelcomeEmailPage /></FeatureProtectedRoute>} />
            <Route path="email-bienvenue/new" element={<FeatureProtectedRoute><WelcomeEmailEditorPage /></FeatureProtectedRoute>} />
            <Route path="email-bienvenue/:id/edit" element={<FeatureProtectedRoute><WelcomeEmailEditorPage /></FeatureProtectedRoute>} />
            <Route
              path="gestion-des-donnees"
              element={<FeatureProtectedRoute><DataManagementPage /></FeatureProtectedRoute>}
            />
            <Route
              path="ayants-droit"
              element={<FeatureProtectedRoute><BeneficiaryTypesPage /></FeatureProtectedRoute>}
            />
            <Route
              path="justificatifs"
              element={<FeatureProtectedRoute><ProofDocumentTypesPage /></FeatureProtectedRoute>}
            />
            <Route
              path="representative-roles"
              element={(
                <FeatureProtectedRoute>
                  <RepresentativeRolesPage />
                </FeatureProtectedRoute>
              )}
            />
            <Route
              path="signature-email"
              element={(
                <FeatureProtectedRoute>
                  <EmailSignaturePage />
                </FeatureProtectedRoute>
              )}
            />
            <Route
              path="config-modules"
              element={(
                <FeatureProtectedRoute>
                  <ModulesConfigPage />
                </FeatureProtectedRoute>
              )}
            />
            <Route
              path="regles"
              element={<FeatureProtectedRoute><RulesPage /></FeatureProtectedRoute>}
            />
          </Route>

          {/* Editor route at Layout level — no ParametersLayout wrapper */}
          <Route
            path={`${PARAMETER_PATH}/tableaux-de-bord/:id/edit`}
            element={(
              <FeatureProtectedRoute>
                <DashboardLayoutEditorPage />
              </FeatureProtectedRoute>
            )}
          />

          <Route
            path={FILE_MANAGER_PATH}
            element={(
              <FeatureProtectedRoute>
                <FileManagerPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={FILE_MANAGER_FOLDER_PATH}
            element={(
              <FeatureProtectedRoute>
                <FileManagerPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={USER_SETTINGS_PATH}
            element={(
              <FeatureProtectedRoute>
                <UserSettingsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={`${USER_SETTINGS_PATH}/notifications`}
            element={(
              <FeatureProtectedRoute>
                <UserSettingsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={`${USER_SETTINGS_PATH}/avatar`}
            element={(
              <FeatureProtectedRoute>
                <UserSettingsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={USER_LINK_ACCOUNTS_PATH}
            element={(
              <FeatureProtectedRoute>
                <UserSettingsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={`${USER_SETTINGS_PATH}/sessions`}
            element={(
              <FeatureProtectedRoute>
                <UserSettingsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route
            path={`${USER_SETTINGS_PATH}/signature`}
            element={(
              <FeatureProtectedRoute>
                <UserSettingsPage />
              </FeatureProtectedRoute>
            )}
          />
          <Route path={RULE_NEW_PATH} element={<RuleEditPage />} />
          <Route path={RULE_EDIT_PATH} element={<RuleEditPage />} />
          <Route path={CUSTOM_FORMS_PAGE_PATH} element={<FeatureProtectedRoute><CustomFormsPage /></FeatureProtectedRoute>} />
          <Route path={CUSTOM_FORM_NEW_PATH} element={<FeatureProtectedRoute><CustomFormEditPage /></FeatureProtectedRoute>} />
          <Route
            path={CUSTOM_FORM_EDIT_PATH}
            element={<FeatureProtectedRoute><CustomFormEditPage /></FeatureProtectedRoute>}
          />
          <Route
            path={CUSTOM_FORM_SUBMISSIONS_PATH}
            element={<FeatureProtectedRoute><CustomFormSubmissionsPage /></FeatureProtectedRoute>}
          />
          {/* Locations (Core::Catalog) routes — déclarées plus haut
              dans le Layout standard (scroll OK). Le bloc isOrganization
              SettingsLayout impose `lg:overflow-hidden` qui cassait le
              scroll sur planning + reservations/:id. */}
        </Route>
      </Route>
    </TrackedRoutes>
  );
};

export default AppRouter;
